import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { DynamicIcon } from '@/utils/iconMap';
import IconPicker from '@/components/IconPicker';
import { API_URL } from '@/config/api';

const newFeature = () => ({
  id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  icon: 'Star',
  title: '',
  description: '',
});

const AdminAboutContent = () => {
  const [content, setContent] = useState({
    hero_title: '',
    hero_subtitle: '',
    intro_1: '',
    intro_2: '',
    features: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API_URL}/about-content`);
      setContent({
        hero_title: res.data.hero_title || '',
        hero_subtitle: res.data.hero_subtitle || '',
        intro_1: res.data.intro_1 || '',
        intro_2: res.data.intro_2 || '',
        features: res.data.features || [],
      });
    } catch (error) {
      console.error('Failed to fetch about content:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const updateFeature = (index, field, value) => {
    const updated = [...content.features];
    updated[index] = { ...updated[index], [field]: value };
    setContent({ ...content, features: updated });
  };

  const addFeature = () => {
    setContent({ ...content, features: [...content.features, newFeature()] });
  };

  const removeFeature = () => {
    if (deleteTarget === null) return;
    setContent({
      ...content,
      features: content.features.filter((_, i) => i !== deleteTarget),
    });
    setDeleteTarget(null);
  };

  const moveFeature = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= content.features.length) return;
    const reordered = [...content.features];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setContent({ ...content, features: reordered });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        id: 'about',
        ...content,
        features: content.features.map((f) => ({
          id: f.id && !f.id.startsWith('temp-') ? f.id : undefined,
          icon: f.icon,
          title: f.title,
          description: f.description,
        })),
      };
      const res = await axios.put(`${API_URL}/about-content`, payload);
      setContent({
        hero_title: res.data.hero_title || '',
        hero_subtitle: res.data.hero_subtitle || '',
        intro_1: res.data.intro_1 || '',
        intro_2: res.data.intro_2 || '',
        features: res.data.features || [],
      });
      toast.success('儲存成功');
    } catch (error) {
      console.error(error);
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">載入中...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">「關於我們」內容管理</h1>
            <p className="text-muted-foreground mt-1">管理關於我們頁面的標題、簡介和特色項目</p>
          </div>
          <Button onClick={handleSave} disabled={saving} size="lg">
            {saving ? '儲存中...' : '儲存全部'}
          </Button>
        </div>

        {/* Hero Section */}
        <Card>
          <CardHeader>
            <CardTitle>頂部橫幅</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>主標題</Label>
              <Input
                value={content.hero_title}
                onChange={(e) => updateField('hero_title', e.target.value)}
                placeholder="例如：關於智駕駛"
              />
            </div>
            <div>
              <Label>副標題</Label>
              <Input
                value={content.hero_subtitle}
                onChange={(e) => updateField('hero_subtitle', e.target.value)}
                placeholder="例如：用心教學，助你輕鬆考取駕駛執照"
              />
            </div>
          </CardContent>
        </Card>

        {/* Intro Paragraphs */}
        <Card>
          <CardHeader>
            <CardTitle>簡介段落</CardTitle>
            <p className="text-sm text-muted-foreground">支援換行，每段獨立一個區塊</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>段落 1</Label>
              <Textarea
                value={content.intro_1}
                onChange={(e) => updateField('intro_1', e.target.value)}
                rows={4}
                placeholder="第一段介紹..."
              />
            </div>
            <div>
              <Label>段落 2</Label>
              <Textarea
                value={content.intro_2}
                onChange={(e) => updateField('intro_2', e.target.value)}
                rows={4}
                placeholder="第二段介紹..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>特色項目</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">展示在頁面下半部份的特色卡片</p>
            </div>
            <Button variant="outline" size="sm" onClick={addFeature}>
              <Plus className="mr-2 h-4 w-4" /> 新增項目
            </Button>
          </CardHeader>
          <CardContent>
            {content.features.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">尚未有特色項目</p>
            ) : (
              <div className="space-y-4">
                {content.features.map((feature, index) => (
                  <div key={feature.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                          <DynamicIcon name={feature.icon} className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="font-semibold">項目 {index + 1}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => moveFeature(index, -1)}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => moveFeature(index, 1)}
                          disabled={index === content.features.length - 1}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => setDeleteTarget(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <IconPicker
                          label="圖示"
                          value={feature.icon}
                          onChange={(v) => updateFeature(index, 'icon', v)}
                        />
                      </div>
                      <div>
                        <Label>標題</Label>
                        <Input
                          value={feature.title}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>描述</Label>
                      <Textarea
                        value={feature.description}
                        onChange={(e) => updateFeature(index, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg">
            {saving ? '儲存中...' : '儲存全部'}
          </Button>
        </div>
      <AlertDialog open={deleteTarget !== null} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除這個項目？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷（儲存後生效）。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={removeFeature} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminAboutContent;
